import { useState } from 'react';
import { capitalizeFirst } from '../../shared/sentence';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { saveTradeAllowed, tradeAllowedOf } from './tradeAllowedApi';

/**
 * Prawo spółki do Trade i giełd (ustalenie ze statusu 29.09.2026).
 *
 * Spółka bez kont giełdowych nie przekazuje zleceń do Trade i nie publikuje
 * ich na giełdach; jej zlecenia zostają w banku do planowania floty. Blokada
 * ma być „łatwa do zdjęcia, gdyby pojawiło się konto” — stąd przełącznik
 * tutaj, z obowiązkowym powodem (trafia do dziennika działań). Zapis wymaga
 * sekcji administracyjnej; przycisk jej nie ukrywa, API odmówi innym.
 */
export const TradeAllowed = ({
  record,
  onSaved,
}: {
  record: Record<string, unknown>;
  onSaved?: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const allowed = tradeAllowedOf(record);
  if (allowed === null) return <span>—</span>;

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
      {allowed ? (
        <Chip size="small" color="success" variant="outlined" label="dozwolone" />
      ) : (
        <Tooltip title="Zleceń tej spółki nie przekazuje się do Trade i nie publikuje na giełdach — brak kont giełdowych. Zlecenia zostają do planowania floty.">
          <Chip size="small" color="warning" variant="outlined" label="zablokowane" />
        </Tooltip>
      )}
      <Button size="small" onClick={() => setOpen(true)}>
        {allowed ? 'Zablokuj' : 'Zdejmij blokadę'}
      </Button>
      {open && (
        <TradeAllowedDialog
          record={record}
          target={!allowed}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false);
            onSaved?.();
          }}
        />
      )}
    </Stack>
  );
};

function TradeAllowedDialog({
  record,
  target,
  onClose,
  onSaved,
}: {
  record: Record<string, unknown>;
  /** Wartość, którą zapisujemy: `true` = zdjęcie blokady. */
  target: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await saveTradeAllowed(record.id as number, target, reason.trim());
      onSaved();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ? capitalizeFirst(msg) : 'Nie udało się zapisać');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {target ? 'Zdjąć blokadę Trade i giełd' : 'Zablokować Trade i giełdy'} —{' '}
        {String(record.name ?? '')}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {target
            ? 'Zlecenia spółki będzie można przekazać do Trade i publikować na giełdach. Zrób to, gdy spółka ma konto na giełdzie.'
            : 'Zleceń spółki nie będzie można przekazać do Trade ani publikować na giełdach. Zlecenia już przekazane zostają, gdzie są.'}
        </Typography>
        <TextField
          label="Powód"
          required
          fullWidth
          size="small"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        {error && (
          <Typography role="alert" color="error" variant="body2" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Anuluj
        </Button>
        <Button onClick={save} variant="contained" disabled={busy || !reason.trim()}>
          Zapisz
        </Button>
      </DialogActions>
    </Dialog>
  );
}
